import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-6">
    <div className="w-full max-w-md rounded-3xl border border-border bg-card p-10 text-center animate-fade-in">{children}</div>
  </div>
);

export const PaymentSuccess = () => {
  const { t } = useApp();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState('polling');
  const p = t.payment;

  useEffect(() => {
    const sid = params.get('session_id') || params.get('subscription_id');
    if (!sid) { setState('error'); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.paymentStatus(sid);
        if (r.payment_status === 'paid') { await refresh(); setState('paid'); return; }
        if (r.status === 'expired' || r.payment_status === 'failed') { setState('error'); return; }
      } catch (e) { console.warn('[payment] status poll failed, retrying', e); }
      if (attempts < 10) timer = setTimeout(poll, 2000); else setState('timeout');
    };
    poll();
    return () => clearTimeout(timer);
    // Intentionally run once on mount: the polling loop owns its own state (attempts/timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Shell>
      {state === 'polling' && <><Loader2 className="h-12 w-12 mx-auto text-primary animate-spin" /><h1 className="mt-6 font-display text-2xl font-bold" data-testid="payment-polling">{p.checking}</h1><p className="mt-2 text-muted-foreground">{p.checkingSub}</p></>}
      {state === 'paid' && <><CheckCircle2 className="h-14 w-14 mx-auto text-primary" /><h1 className="mt-6 font-display text-2xl font-bold" data-testid="payment-success">{p.successTitle}</h1><p className="mt-2 text-muted-foreground">{p.successSub}</p><Button onClick={() => navigate('/chat')} data-testid="payment-continue-btn" className="mt-8 rounded-xl font-semibold gap-2 h-11 px-6">{p.continue} <ArrowRight className="h-4 w-4" /></Button></>}
      {(state === 'error' || state === 'timeout') && <><XCircle className="h-14 w-14 mx-auto text-destructive" /><h1 className="mt-6 font-display text-2xl font-bold" data-testid="payment-error">{state === 'timeout' ? p.timeoutTitle : p.errorTitle}</h1><p className="mt-2 text-muted-foreground">{state === 'timeout' ? p.timeoutSub : p.errorSub}</p><Button onClick={() => navigate('/account')} variant="outline" className="mt-8 rounded-xl font-semibold h-11 px-6">{p.account}</Button></>}
    </Shell>
  );
};

export const PaymentCancel = () => {
  const { t } = useApp();
  const navigate = useNavigate();
  return (
    <Shell>
      <XCircle className="h-14 w-14 mx-auto text-muted-foreground" />
      <h1 className="mt-6 font-display text-2xl font-bold" data-testid="payment-cancel">{t.payment.cancelTitle}</h1>
      <p className="mt-2 text-muted-foreground">{t.payment.cancelSub}</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Button onClick={() => navigate('/upgrade')} className="rounded-xl font-semibold h-11 px-6">{t.payment.retry}</Button>
        <Button onClick={() => navigate('/chat')} variant="outline" className="rounded-xl font-semibold h-11 px-6">{t.payment.back}</Button>
      </div>
    </Shell>
  );
};
